import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import { resolveHero } from '../siteHero';
import { fetchSite, getToken, patchHero, uploadFile } from './api';
import { AdminImagePreview, AdminModal, fileInput, input, label } from './adminUi';

const ICON_OPTIONS = [
  { value: 'hayBale', label: 'Hay / soil' },
  { value: 'corn', label: 'Corn' },
  { value: 'sprout', label: 'Sprout' },
];

const cardClass = 'rounded-xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5';
const primaryBtnClass =
  'rounded-lg bg-brand-olive px-3 py-2 text-sm font-semibold text-white hover:opacity-95 active:opacity-90 disabled:opacity-50';
const secondaryBtnClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';
const ghostBtnClass =
  'rounded-lg px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100';

function ModalFooter({ onCancel, onApply, applyLabel = 'Apply' }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass}>
        Cancel
      </button>
      <button type="button" onClick={onApply} className={primaryBtnClass}>
        {applyLabel}
      </button>
    </div>
  );
}

function clip(s, n) {
  if (typeof s !== 'string' || !s.trim()) return '—';
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

function newHighlightId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `hl-${crypto.randomUUID()}`;
  }
  return `hl-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function ensureHighlightIds(heroForm) {
  return {
    ...heroForm,
    highlights: heroForm.highlights.map((h) => ({
      ...h,
      id: typeof h.id === 'string' && h.id.trim() ? h.id.trim() : newHighlightId(),
    })),
  };
}

function emptySlide() {
  return {
    src: '',
    alt: '',
    label: '',
    title: '',
    subtitle: '',
    ctaLabel: '',
    ctaHref: '',
    slideIntervalMs: 6500,
  };
}

function clampSlideIntervalMs(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 6500;
  return Math.min(120000, Math.max(2000, Math.round(v)));
}

function slideToPayload(s) {
  return {
    src: String(s.src ?? '').trim(),
    alt: String(s.alt ?? '').trim(),
    label: String(s.label ?? '').trim(),
    title: String(s.title ?? '').trim(),
    subtitle: typeof s.subtitle === 'string' ? s.subtitle : '',
    ctaLabel: String(s.ctaLabel ?? '').trim(),
    ctaHref: String(s.ctaHref ?? '').trim(),
    slideIntervalMs: clampSlideIntervalMs(s.slideIntervalMs),
  };
}

function emptyHighlight() {
  return { id: newHighlightId(), line1: '', line2: '', icon: 'hayBale' };
}

export default function HeroDashboard() {
  const navigate = useNavigate();
  const [form, setForm] = useState(() =>
    ensureHighlightIds(resolveHero(DEFAULT_SITE, { editMode: true }))
  );
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingSlide, setUploadingSlide] = useState(false);

  const [modalErr, setModalErr] = useState('');
  const [slideModalOpen, setSlideModalOpen] = useState(false);
  const [slideModalIndex, setSlideModalIndex] = useState(null);
  const [slideDraft, setSlideDraft] = useState(null);

  const [highlightModalOpen, setHighlightModalOpen] = useState(false);
  const [highlightEditId, setHighlightEditId] = useState(null);
  const [highlightDraft, setHighlightDraft] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    let cancelled = false;
    fetchSite()
      .then((site) => {
        if (!cancelled) {
          setForm(ensureHighlightIds(resolveHero(site, { editMode: true })));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setForm(ensureHighlightIds(resolveHero(DEFAULT_SITE, { editMode: true })));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  function closeSlideModal() {
    setSlideModalOpen(false);
    setSlideModalIndex(null);
    setSlideDraft(null);
    setModalErr('');
  }

  function openSlideModal(index) {
    setModalErr('');
    if (index == null) {
      setSlideDraft(emptySlide());
      setSlideModalIndex(null);
    } else {
      const s = form.backgroundSlides[index];
      if (!s) return;
      setSlideDraft({
        ...emptySlide(),
        ...s,
        slideIntervalMs: s.slideIntervalMs ?? 6500,
      });
      setSlideModalIndex(index);
    }
    setSlideModalOpen(true);
  }

  function applySlideModal() {
    if (!slideDraft) return;
    const src = String(slideDraft.src ?? '').trim();
    if (!src) {
      setModalErr('Add an image URL or upload a slide image.');
      return;
    }
    const row = {
      ...slideDraft,
      src,
      alt: String(slideDraft.alt ?? '').trim(),
      label: String(slideDraft.label ?? '').trim(),
      title: String(slideDraft.title ?? '').trim(),
      subtitle: typeof slideDraft.subtitle === 'string' ? slideDraft.subtitle : '',
      ctaLabel: String(slideDraft.ctaLabel ?? '').trim(),
      ctaHref: String(slideDraft.ctaHref ?? '').trim(),
      slideIntervalMs: clampSlideIntervalMs(slideDraft.slideIntervalMs),
    };
    setForm((f) => {
      const slides = [...f.backgroundSlides];
      if (slideModalIndex == null) {
        slides.push(row);
      } else {
        slides[slideModalIndex] = row;
      }
      return { ...f, backgroundSlides: slides };
    });
    closeSlideModal();
  }

  function removeSlide(i) {
    if (!window.confirm('Remove this slide?')) return;
    setForm((f) => ({
      ...f,
      backgroundSlides: f.backgroundSlides.filter((_, j) => j !== i),
    }));
  }

  async function onSlideDraftUpload(file) {
    if (!file) return;
    setErr('');
    setUploadingSlide(true);
    try {
      const { url } = await uploadFile(file);
      setSlideDraft((d) => (d ? { ...d, src: url } : d));
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setUploadingSlide(false);
    }
  }

  function closeHighlightModal() {
    setHighlightModalOpen(false);
    setHighlightEditId(null);
    setHighlightDraft(null);
    setModalErr('');
  }

  function openHighlightModal(id) {
    setModalErr('');
    if (id == null) {
      setHighlightDraft(emptyHighlight());
      setHighlightEditId(null);
    } else {
      const h = form.highlights.find((x) => x.id === id);
      if (!h) return;
      setHighlightDraft({ ...h });
      setHighlightEditId(id);
    }
    setHighlightModalOpen(true);
  }

  function applyHighlightModal() {
    if (!highlightDraft) return;
    const row = {
      id: highlightEditId || highlightDraft.id || newHighlightId(),
      line1: String(highlightDraft.line1 ?? ''),
      line2: String(highlightDraft.line2 ?? ''),
      icon: ICON_OPTIONS.some((o) => o.value === highlightDraft.icon) ? highlightDraft.icon : 'hayBale',
    };
    setForm((f) => {
      if (highlightEditId == null) {
        return { ...f, highlights: [...f.highlights, row] };
      }
      return {
        ...f,
        highlights: f.highlights.map((h) => (h.id === highlightEditId ? row : h)),
      };
    });
    closeHighlightModal();
  }

  function removeHighlight(id) {
    if (!window.confirm('Remove this highlight?')) return;
    setForm((f) => ({
      ...f,
      highlights: f.highlights.filter((h) => h.id !== id),
    }));
  }

  async function save() {
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const slides = form.backgroundSlides
        .filter((s) => s.src && s.src.trim())
        .map(slideToPayload);
      const first = slides[0];
      const payload = {
        label: first?.label ?? '',
        title: first?.title ?? '',
        subtitle: first?.subtitle ?? '',
        ctaLabel: first?.ctaLabel ?? '',
        ctaHref: first?.ctaHref ?? '',
        slideIntervalMs: first ? first.slideIntervalMs : 6500,
        backgroundSlides: slides,
        highlights: form.highlights
          .filter((h) => String(h.line1 ?? '').trim() || String(h.line2 ?? '').trim())
          .map(({ line1, line2, icon, id }) => {
            const row = { line1, line2, icon };
            if (typeof id === 'string' && id.trim()) row.id = id.trim();
            return row;
          }),
      };
      const { hero } = await patchHero(payload);
      setForm(ensureHighlightIds(resolveHero({ hero: hero || payload }, { editMode: true })));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const slides = form.backgroundSlides || [];
  const highlights = form.highlights || [];

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <h1 className="text-lg font-bold text-neutral-900">Hero</h1>
          <p className="text-sm text-neutral-600">Home headline, carousel slides, and highlight chips — edit in dialogs</p>
        </div>
        <button type="button" disabled={saving} onClick={save} className={primaryBtnClass}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 md:px-8">
        {msg ? (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            {msg}
          </p>
        ) : null}
        {err ? (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</p>
        ) : null}

        <div className="w-full space-y-6">
          <section className={cardClass}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="m-0 text-base font-bold text-neutral-900">Background slides</h2>
                <p className="mt-1 text-xs text-neutral-500">
                  {slides.length} slide{slides.length === 1 ? '' : 's'} · first headline: {clip(slides[0]?.title, 48)}
                </p>
              </div>
              <button type="button" onClick={() => openSlideModal(null)} className={secondaryBtnClass}>
                Add slide
              </button>
            </div>
            {slides.length === 0 ? (
              <p className="mt-3 text-sm text-neutral-500">No slides yet. Add one with an image and copy.</p>
            ) : (
              <ul className="mt-4 list-none space-y-2 p-0">
                {slides.map((s, i) => (
                  <li
                    key={`slide-${i}`}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-neutral-100 bg-neutral-50 px-3 py-2"
                  >
                    <div className="h-14 w-24 shrink-0 overflow-hidden rounded-md bg-neutral-200">
                      {s.src ? (
                        <img src={s.src} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-[10px] text-neutral-400">No img</div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="m-0 text-sm font-semibold text-neutral-900">{clip(s.title || s.label, 44)}</p>
                      <p className="mt-0.5 truncate font-mono text-xs text-neutral-600">{clip(s.src, 52)}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button type="button" className={ghostBtnClass} onClick={() => openSlideModal(i)}>
                        Edit
                      </button>
                      <button type="button" className={`${ghostBtnClass} text-red-700`} onClick={() => removeSlide(i)}>
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={cardClass}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="m-0 text-base font-bold text-neutral-900">Highlights</h2>
                <p className="mt-1 text-xs text-neutral-500">Three icon rows under the hero (empty lines are omitted on save)</p>
              </div>
              <button type="button" onClick={() => openHighlightModal(null)} className={secondaryBtnClass}>
                Add highlight
              </button>
            </div>
            {highlights.length === 0 ? (
              <p className="mt-3 text-sm text-neutral-500">No highlights yet.</p>
            ) : (
              <ul className="mt-4 list-none space-y-2 p-0">
                {highlights.map((h) => (
                  <li
                    key={h.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-neutral-100 bg-neutral-50 px-3 py-2 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-neutral-900">{clip(h.line1, 32)}</span>
                      <span className="mx-2 text-neutral-300">·</span>
                      <span className="text-neutral-700">{clip(h.line2, 40)}</span>
                      <span className="ml-2 text-xs text-neutral-500">({h.icon})</span>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button type="button" className={ghostBtnClass} onClick={() => openHighlightModal(h.id)}>
                        Edit
                      </button>
                      <button type="button" className={`${ghostBtnClass} text-red-700`} onClick={() => removeHighlight(h.id)}>
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      <AdminModal
        open={slideModalOpen}
        title={slideModalIndex == null ? 'Add slide' : 'Edit slide'}
        description="Image is required. Eyebrow, title, subtitle, and CTA appear on this slide."
        error={modalErr}
        onClose={closeSlideModal}
        footer={<ModalFooter onCancel={closeSlideModal} onApply={applySlideModal} />}
      >
        {slideDraft ? (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
            <div className="min-w-0 space-y-3">
              <label className={`${label} mb-0`}>
                Slide image URL
                <input
                  className={input}
                  value={slideDraft.src}
                  onChange={(e) => {
                    setSlideDraft((d) => ({ ...d, src: e.target.value }));
                    setModalErr('');
                  }}
                />
              </label>
              <label className={`${label} mb-0`}>
                Upload image
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploadingSlide}
                  className={fileInput}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    onSlideDraftUpload(f);
                  }}
                />
              </label>
              {uploadingSlide ? <p className="text-xs text-neutral-500">Uploading…</p> : null}
              <AdminImagePreview src={slideDraft.src} alt={slideDraft.alt || ''} />
              {slideDraft.src ? (
                <button
                  type="button"
                  onClick={() => setSlideDraft((d) => (d ? { ...d, src: '' } : d))}
                  className="text-xs font-medium text-neutral-600 underline decoration-neutral-300 hover:text-neutral-900"
                >
                  Clear image URL
                </button>
              ) : null}
              <label className={label}>
                Alt (optional)
                <input
                  className={input}
                  value={slideDraft.alt}
                  onChange={(e) => setSlideDraft((d) => ({ ...d, alt: e.target.value }))}
                />
              </label>
            </div>
            <div className="grid min-w-0 gap-3">
              <label className={label}>
                Eyebrow label
                <input className={input} value={slideDraft.label} onChange={(e) => setSlideDraft((d) => ({ ...d, label: e.target.value }))} />
              </label>
              <label className={label}>
                Title
                <input className={input} value={slideDraft.title} onChange={(e) => setSlideDraft((d) => ({ ...d, title: e.target.value }))} />
              </label>
              <label className={label}>
                Subtitle
                <textarea
                  className={`${input} min-h-[5rem]`}
                  value={slideDraft.subtitle}
                  onChange={(e) => setSlideDraft((d) => ({ ...d, subtitle: e.target.value }))}
                />
              </label>
              <label className={label}>
                CTA label
                <input className={input} value={slideDraft.ctaLabel} onChange={(e) => setSlideDraft((d) => ({ ...d, ctaLabel: e.target.value }))} />
              </label>
              <label className={label}>
                CTA link (href)
                <input className={input} value={slideDraft.ctaHref} onChange={(e) => setSlideDraft((d) => ({ ...d, ctaHref: e.target.value }))} />
              </label>
              <label className={label}>
                Slide interval (ms)
                <input
                  type="number"
                  min={2000}
                  max={120000}
                  step={500}
                  className={input}
                  value={slideDraft.slideIntervalMs}
                  onChange={(e) =>
                    setSlideDraft((d) =>
                      d
                        ? {
                            ...d,
                            slideIntervalMs: e.target.value === '' ? '' : Number(e.target.value),
                          }
                        : d
                    )
                  }
                />
              </label>
            </div>
          </div>
        ) : null}
      </AdminModal>

      <AdminModal
        open={highlightModalOpen}
        title={highlightEditId == null ? 'Add highlight' : 'Edit highlight'}
        description="Two lines of text and an icon. Blank rows are dropped when you save the hero."
        error={modalErr}
        onClose={closeHighlightModal}
        footer={<ModalFooter onCancel={closeHighlightModal} onApply={applyHighlightModal} />}
      >
        {highlightDraft ? (
          <div className="grid gap-4 md:grid-cols-2">
            <label className={label}>
              Line 1
              <input
                className={input}
                value={highlightDraft.line1}
                onChange={(e) => setHighlightDraft((d) => ({ ...d, line1: e.target.value }))}
              />
            </label>
            <label className={label}>
              Line 2
              <input
                className={input}
                value={highlightDraft.line2}
                onChange={(e) => setHighlightDraft((d) => ({ ...d, line2: e.target.value }))}
              />
            </label>
            <label className={`${label} md:col-span-2`}>
              Icon
              <select
                className={input}
                value={highlightDraft.icon}
                onChange={(e) => setHighlightDraft((d) => ({ ...d, icon: e.target.value }))}
              >
                {ICON_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        ) : null}
      </AdminModal>
    </>
  );
}
